import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../context/AuthProvider'
import { api } from '../../../lib/api'
import { Button } from '../../../components/ui/button'
import { Loader2, AlertCircle, Pause, Play, Trash2, Edit, Calendar, DollarSign, CreditCard } from 'lucide-react'

export default function RecurringDonations() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [recurringDonations, setRecurringDonations] = useState([])
  const [error, setError] = useState(null)
  const [actionLoading, setActionLoading] = useState(null)

  useEffect(() => {
    if (user?.id) {
      fetchRecurringDonations()
    }
  }, [user?.id])

  const fetchRecurringDonations = async () => {
    try {
      setLoading(true)
      setError(null)
      const donations = await api.getUserRecurringDonations()
      setRecurringDonations(donations || [])
    } catch (err) {
      console.error('Failed to fetch recurring donations:', err)
      setError(err.message || 'Failed to load recurring donations')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-400'
      case 'paused':
        return 'text-yellow-400'
      case 'canceled':
        return 'text-red-600'
      case 'past_due':
        return 'text-orange-400'
      default:
        return 'text-charcoal-muted'
    }
  }

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Active'
      case 'paused':
        return 'Paused'
      case 'canceled':
        return 'Canceled'
      case 'past_due':
        return 'Past Due'
      default:
        return status
    }
  }

  const handlePause = async (id) => {
    try {
      setActionLoading(id)
      await api.pauseRecurringDonation(id)
      await fetchRecurringDonations()
    } catch (err) {
      console.error('Failed to pause donation:', err)
      alert(err.message || 'Failed to pause donation')
    } finally {
      setActionLoading(null)
    }
  }

  const handleResume = async (id) => {
    try {
      setActionLoading(id)
      await api.resumeRecurringDonation(id)
      await fetchRecurringDonations()
    } catch (err) {
      console.error('Failed to resume donation:', err)
      alert(err.message || 'Failed to resume donation')
    } finally {
      setActionLoading(null)
    }
  }

  const handleCancel = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this recurring donation? This action cannot be undone.')) {
      return
    }

    try {
      setActionLoading(id)
      await api.cancelRecurringDonation(id)
      await fetchRecurringDonations()
    } catch (err) {
      console.error('Failed to cancel donation:', err)
      alert(err.message || 'Failed to cancel donation')
    } finally {
      setActionLoading(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold font-display text-charcoal mb-2">
            Recurring Donations
          </h1>
          <p className="text-charcoal-muted">
            Manage your automatic recurring donations
          </p>
        </div>
        <Button onClick={() => navigate('/app/donor/donations')}>
          Back to Donations
        </Button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-red-600 font-semibold mb-1">Error</h3>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      {recurringDonations.length === 0 ? (
        <div className="bg-white border border-surface-border rounded p-8 text-center">
          <p className="text-charcoal-muted mb-4">
            You don't have any recurring donations set up yet.
          </p>
          <Button onClick={() => navigate('/app/donor/donations')}>
            Set Up Recurring Donation
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {recurringDonations.map((donation) => (
            <div
              key={donation.id}
              className="bg-white border border-surface-border rounded p-6 hover:border-gold/30 transition-colors"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold text-charcoal">
                      {donation.parish?.name || 'Unknown Parish'}
                    </h3>
                    <span className={`text-sm font-medium px-2 py-1 rounded ${getStatusColor(donation.status)} bg-opacity-20`}>
                      {getStatusLabel(donation.status)}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-charcoal-muted">
                      <DollarSign className="w-4 h-4" />
                      <span className="font-semibold text-charcoal text-lg">
                        {formatCurrency(donation.amountCents)}
                      </span>
                      <span className="text-charcoal-subtle">
                        {donation.interval === 'weekly' ? 'per week' : 'per month'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-charcoal-muted">
                      <Calendar className="w-4 h-4" />
                      <span>Started {formatDate(donation.createdAt)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-charcoal-muted">
                      <CreditCard className="w-4 h-4" />
                      <span>Payment method on file</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t border-surface-border">
                {donation.status === 'active' && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePause(donation.id)}
                      disabled={actionLoading === donation.id}
                    >
                      {actionLoading === donation.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Pause className="w-4 h-4 mr-2" />
                          Pause
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/app/donor/recurring-donations/${donation.id}/edit`)}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCancel(donation.id)}
                      disabled={actionLoading === donation.id}
                      className="text-red-600 hover:text-red-600 hover:border-red-400"
                    >
                      {actionLoading === donation.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-2" />
                          Cancel
                        </>
                      )}
                    </Button>
                  </>
                )}

                {donation.status === 'paused' && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleResume(donation.id)}
                      disabled={actionLoading === donation.id}
                    >
                      {actionLoading === donation.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Play className="w-4 h-4 mr-2" />
                          Resume
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCancel(donation.id)}
                      disabled={actionLoading === donation.id}
                      className="text-red-600 hover:text-red-600 hover:border-red-400"
                    >
                      {actionLoading === donation.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-2" />
                          Cancel
                        </>
                      )}
                    </Button>
                  </>
                )}

                {donation.status === 'canceled' && (
                  <p className="text-charcoal-subtle text-sm">
                    This recurring donation has been canceled.
                  </p>
                )}

                {donation.status === 'past_due' && (
                  <p className="text-orange-400 text-sm">
                    Payment failed. Please update your payment method.
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
